import { ButtonHTMLAttributes, PropsWithChildren, forwardRef } from 'react';
import { VariantProps, cva } from 'class-variance-authority';
import { cn } from '@/lib/utils';

export const buttonVariants = cva(
  'group flex justify-center items-center cursor-pointer leading-4 tracking-[2px] transition ease-in-out duration-200 outline-none',
  {
    variants: {
      variant: {
        default:
          'bg-primary text-white hover:bg-white hover:text-graphite border-2 border-primary',
        unanimated: 'bg-primary text-white border-2 border-primary',
        outline:
          'border-2 border-primary text-primary hover:text-white hover:bg-primary',
        text: 'border-0 text-primary hover:text-[#9a7900]',
        blue: 'bg-blue text-white hover:bg-white hover:text-white border-2 border-blue',
        outlineBlue:
          'border-2 border-blue text-blue hover:text-white hover:bg-blue',
      },
      size: {
        default:
          'w-full h-[64px] uppercase text-sm font-semibold py-[10px] px-[30px]',
        icon: {
          default: 'h-8 w-8',
        },
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> &
  PropsWithChildren &
  VariantProps<typeof buttonVariants> & {
    className?: string;
    isLoading?: boolean;
  };

// eslint-disable-next-line react/display-name
const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { children, variant, size, className, isLoading, disabled, ...props },
    forwardedRef,
  ) => {
    return (
      <button
        className={cn(buttonVariants({ variant, size, className }), {
          'opacity-40 cursor-not-allowed': isLoading || disabled,
        })}
        disabled={isLoading}
        {...props}
        ref={forwardedRef}>
        {children}
      </button>
    );
  },
);

export default Button;
